import React, { useEffect, useState } from "react";
import { Badge, Button, Icon, IconButton } from "../../components/ui";
import { ACTION_TYPES, BADGE_TONE, CATEGORIES, SECTION_PREVIEW, STALE_DAYS, categoryOf, groupQueue, isStale, typeMeta } from "../actionTypes";
import { int, money } from "../format";
import { DashCard } from "./parts";
import { cardState } from "./cardState";

export function TypeIcon({ type, size = "md" }) {
  const m = typeMeta(type);
  return (
    <span className={`dash-ico dash-ico--${size} dash-tint--${m.tint}`} aria-hidden="true">
      <Icon name={m.icon} size={size === "lg" ? "md" : "sm"} />
    </span>
  );
}

/** The CTA button(s) of one action: the backend's action, plus "Open" when it also has a page. */
export function ActionButtons({ item, cta, busy, size = "sm", primary = false }) {
  const c = item.cta || {};
  const showOpen = c.action && c.action !== "open" && c.href;
  return (
    <span className="dash-act__ctas">
      <Button
        variant={primary ? "primary" : "secondary"}
        size={size}
        loading={busy}
        disabled={item.done}
        data-btn="dashboard-action-cta"
        data-action={c.action || "open"}
        onClick={() => cta.run(item)}
      >
        {c.label || "Open"}
      </Button>
      {showOpen ? (
        <IconButton label="Open" variant="ghost" size={size} disabled={item.done} onClick={() => cta.go(c.href)}>
          <Icon name="ExternalLink" size="sm" />
        </IconButton>
      ) : null}
    </span>
  );
}

function ActionRow({ item, queue, cta }) {
  const tone = item.done ? "muted" : BADGE_TONE[item.tone] || "muted";
  return (
    <div className={`dash-act${item.done ? " is-done" : ""}`} data-key={item.key}>
      <button
        type="button"
        className="dash-chk"
        aria-pressed={!!item.done}
        aria-label={item.done ? `Mark not done: ${item.title}` : `Mark done: ${item.title}`}
        onClick={() => (item.done ? queue.undoDone(item) : queue.markDone(item))}
      >
        <Icon name="Check" size="xs" />
      </button>
      <TypeIcon type={item.type} />
      <div className="dash-act__body">
        <div className="dash-act__t">{item.title}</div>
        {item.subtitle || item.amount_pence ? (
          <div className="dash-act__s">
            {[item.subtitle, item.amount_pence ? money(item.amount_pence) : null].filter(Boolean).join(" · ")}
          </div>
        ) : null}
      </div>
      <Badge size="sm" tone={tone} className="dash-act__when">
        {item.done ? "Done" : item.when?.label || "Today"}
      </Badge>
      <ActionButtons item={item} cta={cta} busy={cta.busyKey === item.key} />
    </div>
  );
}

const catLabel = (cat) => ACTION_TYPES[cat]?.label || "Other";

/** Category tiles: count + urgent count; click filters the queue (click again = all). */
function Strip({ items, filter, setFilter, stripRef }) {
  const open = items.filter((it) => !it.done);
  const present = CATEGORIES.filter((cat) => open.some((it) => categoryOf(it.type) === cat));
  if (!present.length) return null;
  return (
    <div className="dash-strip" ref={stripRef} role="group" aria-label="Filter actions by type">
      {present.map((cat) => {
        const rows = open.filter((it) => categoryOf(it.type) === cat);
        const urgent = rows.filter((it) => it.group === "now" && !isStale(it)).length;
        const stale = rows.filter(isStale).length;
        return (
          <button
            type="button"
            key={cat}
            className="dash-strip__tile"
            aria-pressed={filter === cat}
            data-cat={cat}
            onClick={() => setFilter(filter === cat ? "all" : cat)}
          >
            <TypeIcon type={cat} size="sm" />
            <span className="dash-strip__body">
              <span className="dash-strip__n dash-num">{int(rows.length)}</span>
              <span className="dash-strip__l">{catLabel(cat)}</span>
            </span>
            <span className="dash-strip__meta">
              {urgent ? <span className="dash-strip__urgent dash-num">{urgent} now</span> : null}
              {stale ? <span className="dash-strip__old dash-num">{stale} old</span> : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function GroupHead({ label, count, open, onToggle, tone, controls }) {
  return (
    <button type="button" className="dash-q-group__head dash-q-group__head--btn" aria-expanded={open} aria-controls={controls} onClick={onToggle}>
      <Icon name={open ? "ChevronDown" : "ChevronRight"} size="xs" />
      <span>{label}</span>
      <span className={`dash-num dash-q-group__count${tone ? ` dash-q-group__count--${tone}` : ""}`}>{count}</span>
    </button>
  );
}

/** One category's rows: top 3, then "Show all N". */
function Section({ cat, rows, queue, cta, expandAll, idPrefix }) {
  const [open, setOpen] = useState(true);
  const [all, setAll] = useState(false);
  const shown = expandAll || all ? rows : rows.slice(0, SECTION_PREVIEW);
  const id = `${idPrefix}-${cat}`;
  return (
    <div className="dash-q-group" data-section={cat}>
      <GroupHead label={catLabel(cat)} count={`${rows.filter((r) => !r.done).length} open`} open={open} onToggle={() => setOpen((v) => !v)} controls={id} />
      {open ? (
        <div id={id}>
          {shown.map((it) => <ActionRow key={it.key} item={it} queue={queue} cta={cta} />)}
          {rows.length > shown.length ? (
            <button type="button" className="dash-q-more" onClick={() => setAll(true)}>
              Show all {rows.length} {catLabel(cat).toLowerCase()}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Action queue: category tiles, "Now" (5 most urgent), a section per category
 * (top 3 + Show all) and a collapsed Backlog (> 30 days). `filter` is owned by
 * the tab so the brief tiles can jump straight to a category.
 */
export default function ActionQueue({ queue, cta, filter, setFilter, chipsRef }) {
  const { items, open, query } = queue;
  const [backlogOpen, setBacklogOpen] = useState(false);
  useEffect(() => setBacklogOpen(false), [filter]);
  const state = cardState(query, { what: "your actions", shape: "row", lines: 4 });
  const list = items.filter((it) => filter === "all" || categoryOf(it.type) === filter);
  const g = groupQueue(list);
  const done = items.length - open.length;
  const backlogCount = g.backlog.reduce((s, b) => s + b.rows.length, 0);

  return (
    <DashCard title="Action queue" info="actions" meta={items.length ? `${done} of ${items.length} done` : null}>
      {state || (
        <>
          <Strip items={items} filter={filter} setFilter={setFilter} stripRef={chipsRef} />
          {filter !== "all" ? (
            <div className="dash-q-filter">
              Showing <b>{catLabel(filter)}</b>
              <Button variant="ghost" size="sm" data-btn="dashboard-queue-show-all" onClick={() => setFilter("all")}>Show everything</Button>
            </div>
          ) : null}
          {list.length ? (
            <div className="dash-queue">
              {g.now.length ? (
                <div className="dash-q-group dash-q-group--now" data-section="now">
                  <div className="dash-q-group__head">
                    <span>Now</span>
                    <span className="dash-num">{g.now.filter((r) => !r.done).length} most urgent</span>
                  </div>
                  {g.now.map((it) => <ActionRow key={it.key} item={it} queue={queue} cta={cta} />)}
                </div>
              ) : null}
              {g.sections.map((s) => (
                <Section key={`${filter}-${s.cat}`} cat={s.cat} rows={s.rows} queue={queue} cta={cta} expandAll={filter !== "all"} idPrefix="dash-q" />
              ))}
              {backlogCount ? (
                <div className="dash-q-group dash-q-group--backlog" data-section="backlog">
                  <GroupHead label={`Backlog — older than ${STALE_DAYS} days`} count={`${backlogCount}`} tone="muted" open={backlogOpen} onToggle={() => setBacklogOpen((v) => !v)} controls="dash-q-backlog" />
                  {backlogOpen ? (
                    <div id="dash-q-backlog" className="dash-q-backlog">
                      {g.backlog.map((s) => (
                        <Section key={`b-${filter}-${s.cat}`} cat={s.cat} rows={s.rows} queue={queue} cta={cta} expandAll={false} idPrefix="dash-qb" />
                      ))}
                    </div>
                  ) : (
                    <p className="dash-q-hint">Unpaid balances, errors and requests untouched for over a month. Open when you have time to clear them.</p>
                  )}
                </div>
              ) : null}
            </div>
          ) : (
            <p className="dash-empty">{items.length ? "Nothing of this type." : "Nothing waiting on you. New actions appear here as they come in."}</p>
          )}
        </>
      )}
    </DashCard>
  );
}
